import { useAuth } from "@/core/auth/hooks";
import {
  useChangePasswordMutation,
  useUpdateProfileMutation,
} from "@/core/auth/services/authApi";
import { fieldErrorsFromZod } from "@/core/auth/utils";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { ApiErrorCode, changePasswordSchema, type Locale } from "@repo/schema";
import { LOCALES, localeNativeName } from "@repo/i18n";
import i18n from "@/core/i18n/i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiErrorMessage, getApiErrorCode } from "@/core/api";
import { useLocale } from "@/core/i18n/useLocale";
import { RadioGroup, RadioGroupItem } from "@/modules/shadcn/ui/radio-group";

type PasswordFieldErrors = Partial<
  Record<"currentPassword" | "newPassword" | "confirmPassword", string>
>;

export const AccountPage = () => {
  const { user } = useAuth();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const { t } = useTranslation(["account", "common"]);
  const locale = useLocale();
  const handleLocaleChange = async (next: Locale) => {
    if (next === locale) return;
    await i18n.changeLanguage(next);
    try {
      await updateProfile({ locale: next }).unwrap();
    } catch (error) {
      // The UI already switched; the preference just did not stick for other devices.
      toast.error(apiErrorMessage(error, t("common:languageUpdateFailed")));
    }
  };
  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<PasswordFieldErrors>(
    {},
  );

  useEffect(() => {
    if (user) setFullName(user.fullName);
  }, [user]);

  const handleSave = async () => {
    if (!fullName.trim()) return;
    try {
      await updateProfile({ fullName: fullName.trim() }).unwrap();
      toast.success(t("accountPage.profileUpdated"));
    } catch {
      toast.error(t("accountPage.profileUpdateFailed"));
    }
  };

  const clearPasswordError = (field: keyof PasswordFieldErrors) => {
    setPasswordErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleChangePassword = async () => {
    // Collect all field errors before deciding whether to abort
    const errors: PasswordFieldErrors = {};

    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
    });
    if (!parsed.success) {
      const zodErrors = fieldErrorsFromZod(parsed.error);
      if (zodErrors.currentPassword)
        errors.currentPassword = zodErrors.currentPassword;
      if (zodErrors.newPassword) errors.newPassword = zodErrors.newPassword;
    }

    if (newPassword && newPassword === currentPassword) {
      errors.newPassword = t("accountPage.passwordSameAsCurrent");
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = t("accountPage.passwordsMismatch");
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordErrors({});
    try {
      await changePassword({ currentPassword, newPassword }).unwrap();
      toast.success(t("accountPage.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      if (getApiErrorCode(error) === ApiErrorCode.PASSWORD_CURRENT_INCORRECT) {
        setPasswordErrors({
          currentPassword: apiErrorMessage(error, t("accountPage.currentPasswordIncorrect")),
        });
        return;
      }
      toast.error(apiErrorMessage(error, t("accountPage.passwordChangeFailed")));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[640px] p-6">
      <div className="flex flex-col gap-4">
        <h5 className="text-xl font-semibold">{t("accountPage.accountInfo")}</h5>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t("accountPage.email")}</Label>
          <Input id="email" value={user?.email ?? ""} disabled />
        </div>
        <div className="flex flex-row items-end gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="fullName">{t("accountPage.fullName")}</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <Button
            className="h-11"
            onClick={handleSave}
            disabled={
              isUpdating ||
              !fullName.trim() ||
              fullName.trim() === user?.fullName
            }
          >
            {isUpdating ? t("accountPage.saving") : t("common:actions.save")}
          </Button>
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-4">
        <h5 className="text-xl font-semibold">{t("common:language")}</h5>
        <RadioGroup
          aria-label={t("common:language")}
          value={locale}
          onValueChange={(next) => void handleLocaleChange(next as Locale)}
        >
          {LOCALES.map((lng) => (
            <label key={lng} className="flex cursor-pointer items-center gap-2">
              <RadioGroupItem value={lng} />
              {localeNativeName[lng]}
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="mt-10 flex flex-col gap-4">
        <h5 className="text-xl font-semibold">{t("accountPage.changePassword")}</h5>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="currentPassword">{t("accountPage.currentPassword")}</Label>
          <Input
            id="currentPassword"
            type="password"
            value={currentPassword}
            aria-invalid={!!passwordErrors.currentPassword || undefined}
            aria-describedby={
              passwordErrors.currentPassword
                ? "currentPassword-error"
                : undefined
            }
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              clearPasswordError("currentPassword");
            }}
          />
          {passwordErrors.currentPassword && (
            <p id="currentPassword-error" className="text-xs text-destructive">
              {passwordErrors.currentPassword}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="newPassword">{t("accountPage.newPassword")}</Label>
          <Input
            id="newPassword"
            type="password"
            value={newPassword}
            aria-invalid={!!passwordErrors.newPassword || undefined}
            aria-describedby={
              passwordErrors.newPassword ? "newPassword-error" : undefined
            }
            onChange={(e) => {
              setNewPassword(e.target.value);
              clearPasswordError("newPassword");
            }}
          />
          {passwordErrors.newPassword ? (
            <p id="newPassword-error" className="text-xs text-destructive">
              {passwordErrors.newPassword}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("accountPage.passwordRule")}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirmPassword">{t("accountPage.confirmNewPassword")}</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            aria-invalid={!!passwordErrors.confirmPassword || undefined}
            aria-describedby={
              passwordErrors.confirmPassword
                ? "confirmPassword-error"
                : undefined
            }
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              clearPasswordError("confirmPassword");
            }}
          />
          {passwordErrors.confirmPassword && (
            <p id="confirmPassword-error" className="text-xs text-destructive">
              {passwordErrors.confirmPassword}
            </p>
          )}
        </div>
        <Button
          className="h-11 self-end"
          onClick={handleChangePassword}
          disabled={
            isChangingPassword ||
            !currentPassword ||
            !newPassword ||
            !confirmPassword
          }
        >
          {isChangingPassword ? t("accountPage.saving") : t("accountPage.changePassword")}
        </Button>
      </div>
    </div>
  );
};
